<template>
    <div class="product-gallery-wrapper">
        <div class="product-gallery">
            <div
                v-qa="'desktop-product-gallery'"
                class="desktop-product-gallery"
            >
                <div
                    v-for="(image, index) in firstTwoImages"
                    :key="index"
                    class="product-gallery-item"
                >
                    <Image
                        v-if="image"
                        v-bind="
                            getGenericImageProps({
                                size: image.large,
                                size_x2: image.large_x2,
                            })
                        "
                        :alt="getImageAlt(product, index)"
                        :loading="
                            getImageLoading(
                                getImageIndex(index),
                                1,
                                !isThreeSixtyVisible
                            )
                        "
                        :data-qa-image-index="index"
                        @click="openGalleryModalFromDesktop(index)"
                    />
                </div>

                <div
                    v-if="threedium"
                    class="product-gallery-item threedium-wrapper"
                >
                    <button
                        type="button"
                        class="see-on-hand-toggle"
                        @click="threedium.toggleSeeOnHand"
                    >
                        {{
                            threedium.seeOnHand.value
                                ? 'See ring only'
                                : 'See on hand'
                        }}
                    </button>

                    <ThreediumPure
                        class="product-gallery-item three-sixty-wrapper"
                    />

                    <Transition name="fade">
                        <div
                            v-if="threedium.isPrepared.value"
                            class="hand-mode-overlay"
                        >
                            {{ rotatitonTipThreedium }}
                        </div>
                    </Transition>

                    <Transition name="fade">
                        <LoadingAnimation
                            v-if="!threedium.isPrepared.value"
                            class="loader"
                        />
                    </Transition>
                </div>

                <div
                    v-else-if="threeSixty.length > 0"
                    class="product-gallery-item"
                >
                    <div
                        ref="desktopThreeSixty"
                        v-qa="'three-sixty-wrapper'"
                        class="three-sixty-wrapper"
                    >
                        <ThreeSixty
                            :images="threeSixty"
                            :initial-image-index="
                                threeSixtySlide !== undefined
                                    ? threeSixtySlide
                                    : initialThreeSixtyImageIndex
                            "
                            :external-image-index="threeSixtySlide"
                            :alt-getter="threeSixtyAltGetter"
                            @index="threeSixtySlide = $event"
                        />

                        <div class="legend">
                            {{ rotatitonTip }}
                        </div>
                    </div>
                </div>

                <div
                    v-if="video"
                    class="product-gallery-item"
                >
                    <AutoplayVideo
                        v-qa="'gallery-video'"
                        :video
                        class="video"
                        @click="openGalleryModalVideoFromDesktop()"
                    />
                </div>

                <div
                    v-for="(image, index) in restOfTheImages"
                    :key="index"
                    class="product-gallery-item"
                >
                    <Image
                        v-if="image"
                        v-bind="
                            getGenericImageProps({
                                size: image.large,
                                size_x2: image.large_x2,
                                alt: getImageAlt(product, index),
                                loading: getImageLoading(
                                    getImageIndex(index),
                                    1,
                                    !isThreeSixtyVisible
                                ),
                            })
                        "
                        :data-qa-image-index="index + 2"
                        @click="openGalleryModalFromDesktop(index + 2)"
                    />
                </div>

                <div
                    v-if="seeInStoreCta"
                    class="product-gallery-item"
                >
                    <GalleryStoreItem
                        @click="
                            openGalleryModalFromDesktop(
                                restOfTheImages.length + 2
                            )
                        "
                    />
                </div>
            </div>

            <div
                v-qa="'mobile-product-gallery'"
                class="mobile-product-gallery"
            >
                <div class="viewer">
                    <div
                        ref="container"
                        class="carousel"
                    >
                        <AutoplayVideo
                            v-if="video"
                            v-qa="'gallery-video'"
                            :video
                            class="slide"
                        />

                        <div
                            v-for="(image, index) in product.images"
                            :key="index"
                            ref="elements"
                            class="slide"
                        >
                            <Image
                                v-bind="
                                    getGenericImageProps({
                                        size: image.large_x2,
                                        size_x2: image.large_x2,
                                        alt: getImageAlt(product, index),
                                        loading: getImageLoading(
                                            getImageIndex(index),
                                            1,
                                            !isThreeSixtyVisible
                                        ),
                                    })
                                "
                                :data-qa-image-index="index"
                                @click="openGalleryModalFromMobile"
                            />
                        </div>

                        <div
                            v-if="seeInStoreCta"
                            class="slide"
                        >
                            <GalleryStoreVideoItem
                                @click="openGalleryModalFromMobile"
                            />
                        </div>
                    </div>

                    <div
                        v-if="isThreeSixtyVisible"
                        v-qa="'three-sixty-wrapper'"
                        class="three-sixty-wrapper"
                    >
                        <ThreeSixty
                            v-if="threeSixty.length > 0"
                            :images="threeSixty"
                            :initial-image-index="
                                threeSixtySlide !== undefined
                                    ? threeSixtySlide
                                    : undefined
                            "
                            :external-image-index="threeSixtySlide"
                            :alt-getter="threeSixtyAltGetter"
                            @click="openGalleryModalV360"
                            @index="threeSixtySlide = $event"
                        />

                        <div class="legend">
                            {{ rotatitonTip }}
                        </div>
                    </div>

                    <button
                        v-if="threeSixty.length > 0"
                        v-qa="
                            isThreeSixtyVisible
                                ? 'gallery-switcher'
                                : 'v360-switcher'
                        "
                        class="gallery-switcher"
                        type="button"
                        @click="toggleViewer"
                    >
                        <span
                            v-if="isThreeSixtyVisible"
                            class="view-gallery"
                        >
                            {{ t('gallery.view_gallery') }}
                        </span>

                        <span
                            v-else
                            class="view-360"
                        >
                            {{ t('gallery.view_360') }}
                        </span>
                    </button>

                    <ButtonRound
                        v-if="!isThreeSixtyVisible"
                        :disabled="isAtStart"
                        class="button-round left"
                        :aria-label="t('catalogue.carousel.button_prev')"
                        icon="angle-left"
                        @click="previous"
                    />

                    <ButtonRound
                        v-if="!isThreeSixtyVisible"
                        :disabled="isAtEnd"
                        class="button-round right"
                        icon="angle-right"
                        :aria-label="t('catalogue.carousel.button_next')"
                        @click="next"
                    />

                    <LabelLabGrown
                        v-if="isLabGrown && !isThreeSixtyVisible"
                        class="label-lab-grown"
                    />

                    <div
                        class="magnifier"
                        @click="openGalleryModalFromMobile"
                    ></div>
                </div>

                <CarouselScrollIndicator
                    v-show="!isThreeSixtyVisible"
                    :count="
                        product.images.length +
                        (video ? 1 : 0) +
                        (seeInStoreCta ? 1 : 0)
                    "
                    :scroll-percent
                />
            </div>
        </div>

        <GalleryModal
            v-if="isOpen"
            v-model:is-three-sixty-visible="isThreeSixtyVisible"
            v-model:three-sixty-slide="threeSixtySlide"
            v-qa="'gallery-modal'"
            :current-external="currentGalleryModal || currentDesktop"
            :product
            :see-in-store-cta="seeInStoreCta"
            @update:current-external="setCurrent"
            @close="closeGalleryModal"
        />
    </div>
</template>

<script lang="ts" setup>
import {
    computed,
    defineAsyncComponent,
    hydrateOnVisible,
    type Ref,
    ref,
    toRef,
    watch,
} from 'vue'
import { useI18n } from 'vue-i18n'

import type { ProductGalleryFragment } from '@/graphql/api'

import { assertNever } from '@/helpers'
import { useCarousel } from '../carousels/base/useCarouselNew'
import { useIsMobileAgent } from '@/plugins/is-mobile-agent.ts'
import { isSuffixLabGrown } from '@/domain/product/isSuffixLabGrown.ts'
import { getGenericImageProps } from '@/components/useImageProps'
import { useClickWithoutDrag } from '@/composables/motion/click-without-drag.ts'
import { usePinchToZoom } from '@/composables/interaction/usePinchToZoom'

import ThreeSixty from '@/components/layout/three-sixty/ThreeSixty.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import Image from '@/components/Image.vue'
import AutoplayVideo from '@/components/AutoplayVideo.vue'
import LabelLabGrown from '../LabelLabGrown.vue'
import CarouselScrollIndicator from '@/components/layout/carousels/base/CarouselScrollIndicator.vue'
import ThreediumPure from '@/views/product/engagement-ring/ThreediumPure.vue'
import { type ThreediumControls } from '@/views/product/engagement-ring/threedium.ts'
import GalleryStoreItem from '@/components/layout/gallery/GalleryStoreItem.vue'
import GalleryStoreVideoItem from '@/components/layout/gallery/GalleryStoreVideoItem.vue'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'

const GalleryModal = defineAsyncComponent({
    loader: () => import('./GalleryModal.vue'),
    hydrate: hydrateOnVisible(),
})

const props = defineProps<{
    product: ProductGalleryFragment
    seeInStoreCta?: boolean
    initialThreeSixtyImageIndex?: number
    threedium?: ThreediumControls
}>()

const isOpen = ref(false)

function openGalleryModalFromMobile() {
    switchToGallery()

    isOpen.value = true
}

function openGalleryModalFromDesktop(index: number) {
    if (video.value) {
        index += 1
    }

    switchToGallery()
    currentGalleryModal.value = index
    currentDesktop.value = index
    isOpen.value = true
}

function openGalleryModalVideoFromDesktop() {
    openGalleryModalFromDesktop(-1)
}

function openGalleryModalV360() {
    switchToThreeSixty()
    isOpen.value = true
}

function closeGalleryModal() {
    isOpen.value = false
}

const isLabGrown = computed(() => isSuffixLabGrown(props.product))

const video = computed(() =>
    'video' in props.product ? props.product.video : null
)

const threeSixtyAltGetter = (index: number): string => {
    return getImageAlt(props.product, `360 degree image ${index}`)
}

const threeSixty = computed(() =>
    'images360' in props.product ? props.product.images360 : []
)
const isThreeSixtyVisible = ref(false)
const threeSixtySlide: Ref<number | undefined> = ref(undefined)

const switchToThreeSixty = () => {
    if (threeSixty.value.length === 0) {
        return
    }

    isThreeSixtyVisible.value = true
}
const switchToGallery = () => (isThreeSixtyVisible.value = false)
const toggleViewer = () => {
    if (isThreeSixtyVisible.value) {
        switchToGallery()
    } else {
        switchToThreeSixty()
    }
}

const desktopThreeSixty: Ref<HTMLElement | null> = ref(null)

useClickWithoutDrag(desktopThreeSixty, () => {
    if (undefined === threeSixtySlide.value) {
        threeSixtySlide.value = props.initialThreeSixtyImageIndex
    }

    openGalleryModalV360()
})

const {
    previous,
    next,
    container,
    elements,
    current: currentGalleryModal,
    isAtStart,
    isAtEnd,
    scrollPercent,
} = useCarousel(
    computed(
        () =>
            props.product.images.length +
            Number(Boolean(video.value)) +
            Number(Boolean(props.seeInStoreCta))
    )
)
const currentDesktop = ref(currentGalleryModal.value)

usePinchToZoom(elements, true)

function setCurrent(index: number): void {
    currentGalleryModal.value = index
    currentDesktop.value = index
}

watch(currentGalleryModal, (n) => {
    currentDesktop.value = n
})

const firstTwoImages = computed(() =>
    threeSixty.value.length
        ? props.product.images.slice(0, 2)
        : [props.product.images[1], props.product.images[2]]
)

const restOfTheImages = computed(() =>
    threeSixty.value.length
        ? props.product.images.slice(2)
        : [props.product.images[0], ...props.product.images.slice(3)]
)

const getImageLoading = (
    key: number,
    thresholdEager = 6,
    conditionEager = true
) => (conditionEager && key < thresholdEager ? 'eager' : 'lazy')

const getImageIndex = (index: number) => (video.value ? index + 1 : index)

const { t } = useI18n()
const isMobileAgent = useIsMobileAgent()

const rotatitonTip = computed(() =>
    isMobileAgent ? t('gallery.swipe_to_rotate') : t('gallery.drag_to_rotate')
)

const seeOnHand = toRef(() => props.threedium?.seeOnHand.value)
const rotatitonTipThreedium = computed(() =>
    isMobileAgent
        ? seeOnHand.value
            ? t('gallery.threedium.hand_view.swipe_to_rotate')
            : t('gallery.threedium.ring_view.swipe_to_rotate')
        : seeOnHand.value
          ? t('gallery.threedium.hand_view.drag_to_rotate')
          : t('gallery.threedium.ring_view.drag_to_rotate')
)
</script>

<script lang="ts">
const getImageAlt = (ring: ProductGalleryFragment, suffix: number | string) => {
    switch (ring.__typename) {
        case 'CatalogueEngagementRing':
            // TODO translate
            return `Taylor & Hart ${ring.productName} ${ring.centreStoneConstraints.shape.translation.name} Engagement Ring ${suffix}`
        case 'CatalogueReadyToGoRing':
            // TODO translate
            return `Taylor &amp; Hart ${ring.productName} Ready to Go Ring ${suffix}`
        case 'CatalogueWeddingRing':
            // TODO translate
            return `Taylor & Hart ${ring.productName} Wedding Ring ${suffix}`
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
        // fallthrough
        case 'CatalogueJewelleryRing':
            return `Taylor & Hart ${ring.productName} Jewellery ${suffix}`
        default:
            // TODO translate
            return assertNever(ring, 'Taylor & Hart Ring')
    }
}
</script>

<style lang="scss" scoped>
.product-gallery-wrapper {
    align-self: stretch;
}

.product-gallery {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 0 auto;
    max-width: 480px;

    @include min-width(768) {
        max-width: initial;
    }

    @include min-width(1024) {
        position: sticky;
        align-self: flex-end;
        bottom: 20px;
    }

    // Stick to the top on large (height) screens
    @media screen and (min-width: 1024px) and (min-height: 800px) {
        bottom: auto;
        top: 180px;
        align-self: flex-start;
    }

    .three-sixty-wrapper {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f9f9f9;
        aspect-ratio: 1;

        .legend {
            position: absolute;
            bottom: 16px;
            text-align: center;
            font-family: $effra-font-stack;
            letter-spacing: 0.02em;
            text-transform: uppercase;
            font-weight: 400;
            font-size: 0.75rem;
            color: $grey-500;

            @include min-width(1024) {
                bottom: auto;
                top: 10px;
            }

            @include min-width(1280) {
                top: 40px;
            }

            &::before {
                content: '';
                background: url('/src/assets/icons/swipe-icon.svg') no-repeat
                    center center;
                background-size: 30px auto;
                width: 100%;
                height: 30px;
                display: block;
                opacity: 0.6;
            }
        }
    }

    .desktop-product-gallery {
        width: 100%;
        flex-wrap: nowrap;
        align-items: flex-start;
        display: none;

        @include min-width(1024) {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            grid-template-rows: auto;
            grid-auto-rows: auto;
            gap: 20px;
        }

        .product-gallery-item {
            aspect-ratio: 1;
            grid-column: span 3;
            grid-row: span 3;
            background: #f9f9f9;
            cursor: pointer;

            &.threedium-wrapper {
                position: relative;

                .see-on-hand-toggle {
                    position: absolute;
                    width: 140px;
                    height: 36px;
                    border-radius: 18px;
                    text-align: center;
                    z-index: 2;
                    top: 20px;
                    left: 0;
                    right: 0;
                    margin: 0 auto;
                    font-family: $effra-font-stack;
                    font-size: 0.75rem;
                    line-height: 20px;
                    color: $white;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                    font-weight: 400;
                    background-color: $blue;
                    backdrop-filter: blur(5px);
                    user-select: none;
                    cursor: pointer;
                    border: none;
                }

                .hand-mode-overlay {
                    position: absolute;
                    bottom: 16px;
                    left: 50%;
                    transform: translateX(-50%);

                    text-align: center;
                    font-family: $effra-font-stack;
                    letter-spacing: 0.02em;
                    text-transform: uppercase;
                    font-weight: 400;
                    font-size: 0.75rem;
                    color: $grey-500;
                    z-index: 2;

                    @include min-width(1280) {
                        bottom: 60px;
                    }

                    &::before {
                        content: '';
                        background: url('/src/assets/icons/swipe-icon.svg')
                            no-repeat center center;
                        background-size: 30px auto;
                        width: 100%;
                        height: 30px;
                        display: block;
                        opacity: 0.6;
                    }
                }

                .loader {
                    position: absolute;
                    top: 0;
                    height: 100%;
                    z-index: 3;

                    background-color: #f9f9f9;
                }
            }

            :deep(.image) {
                mix-blend-mode: darken;
            }

            &:nth-child(1) {
                grid-column: span 6;
                grid-row: span 6;

                @include min-width(1024) {
                    grid-column: 1 / span 2;
                    grid-row: 1;
                }
            }

            &:nth-child(2) {
                grid-column: 1 / span 2;
                grid-row: 2;
            }

            &:nth-child(3) {
                grid-column: 3 / span 4;
                grid-row: 1 / span 2;
            }

            &:nth-child(4) {
                grid-column: 1 / span 4;
                grid-row: 3 / span 2;
            }

            &:nth-child(5) {
                grid-column: 5 / span 2;
                grid-row: 3;
            }

            &:nth-child(6) {
                grid-column: 5 / span 2;
                grid-row: 4;
            }

            &:nth-child(7) {
                grid-column: 1 / span 3;
                grid-row: 5;
            }

            &:nth-child(8) {
                grid-column: 4 / span 3;
                grid-row: 5;
            }

            &:nth-child(n + 9) {
                grid-column: auto / span 2;
                grid-row: auto / span 1;
            }
        }
    }

    .mobile-product-gallery {
        margin: 0 -20px;
        max-width: initial;
        width: calc(100% + 40px);

        @include min-width(768) {
            margin: 0;
        }

        @include min-width(1024) {
            display: none;
        }

        .three-sixty-wrapper {
            position: absolute;
            width: 100%;
            height: 100%;
        }

        .viewer {
            width: 100%;
            height: auto;
            aspect-ratio: 1/1;
            display: flex;
            align-items: center;
            position: relative;

            .carousel {
                position: relative;
                display: flex;
                overflow-x: auto;
                overflow-y: hidden;
                scroll-snap-type: x mandatory;
                width: 100%;
                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */

                @supports (-webkit-touch-callout: none) {
                    /* Prevent slide vertical scroll in iOS */
                    overscroll-behavior-y: none;
                }

                &::-webkit-scrollbar {
                    display: none;
                }

                .slide {
                    scroll-snap-align: start;
                    flex: 0 0 100%;
                    // height: auto;
                    user-select: none;
                    line-height: 0;

                    background: #f9f9f9;

                    :deep(.image) {
                        mix-blend-mode: darken;
                    }

                    &.has-click {
                        opacity: 0.3;
                        filter: blur(1px);
                        transform: scale3d(1.25, 1.25, 1)
                            translate3d(0px, 0px, 0px) !important;
                        transition:
                            opacity 0.35s ease-in-out,
                            blur 0.7s ease-in-out,
                            transform 0.35s ease-in-out;
                    }

                    &.is-closing {
                        transition:
                            opacity 0.35s ease-in-out,
                            blur 0.7s ease-in-out,
                            transform 0.35s ease-in-out;
                    }
                }
            }

            .gallery-switcher {
                position: absolute;
                height: 28px;
                border-radius: 4px;
                text-align: center;
                top: 12px;
                left: 8px;
                font-family: $effra-font-stack;
                font-size: 0.75rem;
                line-height: 20px;
                color: $blue;
                text-transform: uppercase;
                letter-spacing: 0.02em;
                font-weight: 500;
                background-color: rgba(249, 249, 249, 0.7);
                user-select: none;
                padding: 0 10px;

                .view-360 {
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    &:after {
                        font-family: $icon-font;
                        content: $icon-angle-right;
                        font-weight: 900;
                        font-size: 9px;
                        display: block;
                        margin-left: 5px;
                    }
                }

                .view-gallery {
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    &:after {
                        font-family: $icon-font;
                        content: $icon-angle-right;
                        font-weight: 900;
                        font-size: 9px;
                        display: block;
                        margin-left: 5px;
                    }
                }
            }

            .view-switcher {
                position: absolute;
                top: 16px;
                right: 16px;
                padding: 8px 12px;
            }

            .button-round {
                position: absolute;
                z-index: 3;
                top: calc(50% - 20px);
                width: 40px;
                height: 40px;
                font-size: 1rem;
                color: $blue;

                border-radius: 50%;
                background-color: rgb(249, 249, 249, 0.7);

                &.disabled {
                    display: none;
                }

                &.left {
                    left: 8px;
                }

                &.right {
                    right: 8px;
                }
            }

            .label-lab-grown {
                position: absolute;
                bottom: 12px;
                right: 12px;
            }

            .magnifier {
                position: absolute;
                top: 12px;
                right: 14px;
                width: 28px;
                height: 28px;
                cursor: pointer;
                background-color: rgba(249, 249, 249, 0.6);
                border-radius: 3px;
                padding: 0 6px;

                &::before {
                    content: '';
                    background: url('/src/assets/icons/enlarge.svg') no-repeat
                        center center;
                    background-size: contain;
                    width: 100%;
                    height: 100%;
                    display: block;
                }
            }
        }
    }
}

.fade-leave-active {
    transition: opacity 0.65s ease-in;
}

.fade-leave-to {
    opacity: 0;
}
</style>
